import { useAppContext } from '@/Main';
import { getColor } from '@/helpers/utils';

const VIEW_W = 400;
const VIEW_H = 238;
const CX = 200;
const CY = 198;
const RADIUS = 118;
const STROKE = 13;
const LABEL_RADIUS = RADIUS + 22;
const NEEDLE_TIP = RADIUS - STROKE / 2 - 12;
const NEEDLE_BASE = 46;

export type ScoreGaugeZone = {
  id: string;
  color: string;
  label: string;
};

export const AI_LIKENESS_GAUGE_ZONES: ScoreGaugeZone[] = [
  { id: 'ai', color: '#fb7185', label: 'AI' },
  { id: 'ai-mid', color: '#facc15', label: 'بیشتر AI' },
  { id: 'human-mid', color: '#38bdf8', label: 'بیشتر انسان' },
  { id: 'human', color: '#34d399', label: 'انسان' }
];

export const SEO_SCORE_GAUGE_ZONES: ScoreGaugeZone[] = [
  { id: 'poor', color: '#fb7185', label: 'ضعیف' },
  { id: 'fair', color: '#facc15', label: 'متوسط' },
  { id: 'good', color: '#38bdf8', label: 'خوب' },
  { id: 'excellent', color: '#34d399', label: 'عالی' }
];

export const getScoreGaugeVerdict = (
  score: number,
  zones: ScoreGaugeZone[] = SEO_SCORE_GAUGE_ZONES
) => {
  const clamped = Math.min(Math.max(score, 0), 100);
  const slice = 100 / zones.length;
  const index = Math.min(Math.floor(clamped / slice), zones.length - 1);
  return zones[index].label;
};

const buildSegments = (zones: ScoreGaugeZone[]) =>
  zones.map((zone, index) => {
    const slice = 180 / zones.length;
    return {
      zone: zone.id,
      from: 180 - index * slice,
      to: 180 - (index + 1) * slice
    };
  });

const buildLabels = (zones: ScoreGaugeZone[]) =>
  zones.map((zone, index) => {
    const slice = 180 / zones.length;
    return {
      zone: zone.id,
      angle: 180 - index * slice - slice / 2,
      text: zone.label
    };
  });

const zoneIdForAngle = (angle: number, zones: ScoreGaugeZone[]) => {
  const slice = 180 / zones.length;
  const index = Math.min(
    Math.max(Math.floor((180 - angle) / slice), 0),
    zones.length - 1
  );
  return zones[index].id;
};

const TICKS = Array.from({ length: 21 }, (_, index) => 180 - index * 9);

const polar = (angleDeg: number, radius: number) => {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: CX + radius * Math.cos(rad),
    y: CY - radius * Math.sin(rad)
  };
};

const arcPath = (fromDeg: number, toDeg: number) => {
  const start = polar(fromDeg, RADIUS);
  const end = polar(toDeg, RADIUS);
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${RADIUS} ${RADIUS} 0 0 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
};

type AiLikenessGaugeProps = {
  value: number;
  displayValue: string;
  zones?: ScoreGaugeZone[];
};

const AiLikenessGauge = ({
  value,
  displayValue,
  zones = AI_LIKENESS_GAUGE_ZONES
}: AiLikenessGaugeProps) => {
  const {
    config: { isDark }
  } = useAppContext() as { config: { isDark: boolean } };

  const zoneColors = Object.fromEntries(
    zones.map(zone => [zone.id, zone.color])
  ) as Record<string, string>;
  const segments = buildSegments(zones);
  const labels = buildLabels(zones);
  const clamped = Math.min(Math.max(value, 0), 100);
  const rotation = (clamped / 100) * 180 - 90;
  const needleColor = isDark ? '#edf2f9' : getColor('gray-500');
  const tip = polar(90, NEEDLE_TIP);
  const baseLeft = polar(96.8, NEEDLE_BASE);
  const baseRight = polar(83.2, NEEDLE_BASE);

  return (
    <div
      className="position-relative w-100 px-2 ai-likeness-gauge"
      style={{ direction: 'ltr' }}
    >
      <svg
        aria-hidden="true"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="d-block w-100"
        style={{ overflow: 'visible' }}
      >
        {segments.map(segment => (
          <path
            key={segment.zone}
            d={arcPath(segment.from, segment.to)}
            fill="none"
            stroke={zoneColors[segment.zone]}
            strokeWidth={STROKE}
            strokeLinecap="butt"
          />
        ))}

        {TICKS.map(angle => {
          const isMajor = angle % 45 === 0;
          const inner = polar(angle, RADIUS - (isMajor ? 14 : 9));
          const outer = polar(angle, RADIUS);
          return (
            <line
              key={angle}
              x1={inner.x}
              y1={inner.y}
              x2={outer.x}
              y2={outer.y}
              stroke={zoneColors[zoneIdForAngle(angle, zones)]}
              strokeLinecap="round"
              strokeWidth={isMajor ? 3 : 1.5}
            />
          );
        })}

        <g transform={`rotate(${rotation} ${CX} ${CY})`}>
          <path
            d={`M ${baseLeft.x.toFixed(2)} ${baseLeft.y.toFixed(2)} L ${tip.x.toFixed(2)} ${tip.y.toFixed(2)} L ${baseRight.x.toFixed(2)} ${baseRight.y.toFixed(2)} Z`}
            fill={needleColor}
          />
        </g>
      </svg>

      {labels.map(label => {
        const point = polar(label.angle, LABEL_RADIUS);
        return (
          <span
            key={label.zone}
            className="position-absolute fw-bold fs--2"
            style={{
              left: `${(point.x / VIEW_W) * 100}%`,
              top: `${(point.y / VIEW_H) * 100}%`,
              transform: `translate(-50%, -50%) rotate(${90 - label.angle}deg)`,
              color: zoneColors[label.zone],
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              lineHeight: 1.2
            }}
          >
            {label.text}
          </span>
        );
      })}

      <div
        className="position-absolute text-center"
        style={{
          left: '50%',
          top: `${((CY - 4) / VIEW_H) * 100}%`,
          transform: 'translate(-50%, -35%)',
          pointerEvents: 'none'
        }}
      >
        <div className="fw-bold text-1100 lh-1" style={{ fontSize: '1.9rem' }}>
          {displayValue}
        </div>
      </div>
    </div>
  );
};

export default AiLikenessGauge;
